import { FC, useEffect, useState } from "react";
import { RouterProvider } from "react-router-dom";

import { ProcedureContext } from "@/context/ProcedureContext";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { TourProvider } from "@reactour/tour";

import { Loading } from "@/components/Application/Loading";
import { TimeProvider } from "@/components/Application/TimeContext";
// Import from component
import { ColorModeContext, useMode } from "@/components/Application/theme";
import { router } from "@/components/Routes/Routes";

import { Language } from "@/model/Language/Language";
// Import from model
import { TranslationProvider } from "@/model/Translation/TranslationContext";
import { User } from "@/model/User/User";
import { UserContextProvider } from "@/model/User/UserContext";
import { UserCompanyConfiguration } from "@/model/UserCompanyConfiguration/UserCompanyConfiguration";
import { UserCompanyConfigurationProvider } from "@/model/UserCompanyConfiguration/UserCompanyConfigurationContext";

// Import from service
import {
	checkIfAtLeastGuestAccess,
	useFetchApplicationStatus,
	useFetchActiveUser,
	useFetchActiveUserCompanyConfiguration
} from "@/service/ApplicationQueries";
import { UserNotActive } from "@/components/Application/UserNotActive";

export const Satos: FC = () => {
	const [appUser, setAppUser] = useState<User | null>(null);
	const [companyConfiguration, setCompanyConfiguration] =
		useState<UserCompanyConfiguration | null>(null);
	const [language, setLanguage] = useState<Language | null>(null);
	const [openFolders, setOpenFolders] = useState<string[]>([]);
	const [expandAll, setExpandAll] = useState<boolean>(false);
	const [activeStep, setActiveStep] = useState<number>(-1);

	const { isLoading: isServerCheckLoading, data: serverResponding } =
		useFetchApplicationStatus();

	const { isLoading: isUserLoading, data: user } =
		useFetchActiveUser(serverResponding?.IsActiveUser);

	const { data: userCompanyConfiguration } =
		useFetchActiveUserCompanyConfiguration(serverResponding?.IsActiveUser);

	useEffect(() => {
		if (!isServerCheckLoading && !serverResponding?.IsActiveUser) {
			console.log("reloading application");
			window.location.reload();
		}
	}, [serverResponding]);

	useEffect(() => {
		if (user) {
			setAppUser(user);
		}
		if (user?.Company?.Language) {
			setLanguage(user.Company.Language);
		}
	}, [user]);

	useEffect(() => {
		if (userCompanyConfiguration) {
			setCompanyConfiguration(userCompanyConfiguration);
		}
	}, [userCompanyConfiguration]);

	const { theme, colorMode } = useMode();
	const { toggleColorMode, mode } = colorMode;

	return (
		<ColorModeContext.Provider value={{ toggleColorMode, mode }}>
			<TimeProvider>
				<ThemeProvider theme={theme}>
					<TourProvider
						steps={[]}
						styles={{
							popover: (base) => ({
								...base,
							}),
						}}
					>
						<CssBaseline />
						<TranslationProvider value={language}>
							<ProcedureContext.Provider
								value={{ openFolders, setOpenFolders, expandAll, setExpandAll, activeStep, setActiveStep }}
							>
								<UserContextProvider value={appUser}>
									<UserCompanyConfigurationProvider
										value={companyConfiguration}
									>
										{user && checkIfAtLeastGuestAccess(user) ? (
											<RouterProvider router={router} />
										) : (
											<Loading loading={isUserLoading}>
												<UserNotActive />
											</Loading>
										)}
									</UserCompanyConfigurationProvider>
								</UserContextProvider>
							</ProcedureContext.Provider>
						</TranslationProvider>
					</TourProvider>
				</ThemeProvider>
			</TimeProvider>
		</ColorModeContext.Provider>
	);
};

